import DeleteModal from '@/components/modal/delete-modal'
import { Button } from '@/components/ui/button'
import { getFileIconFromUrl } from '@/components/ui/file-icon'
import URLViewerModal from '@/components/url-viewer-modal'
import useSignedUrl from '@/hooks/use-signed-url'
import { extractFileNameFromUrl } from '@/utils/file-utils'
import { Download, LoaderCircle, Trash2 } from 'lucide-react'
import { useState } from 'react'

export const URLPreviewCard = ({
  url,
  onDelete,
  deleting,
  hideRemoveButton = false,
  onSuccess,
}: {
  url: string | null
  onDelete?: () => Promise<void>
  hideRemoveButton?: boolean
  deleting?: boolean
  onSuccess?: () => void
}) => {
  const { signedUrl, isLoading } = useSignedUrl(url)
  const [showFileViewer, setShowFileViewer] = useState(false)
  const [openDeleteModal, setOpenDeleteModal] = useState(false)

  if (!url?.trim()) return null
  if (isLoading) return null
  if (!signedUrl) return null

  const FileIcon = getFileIconFromUrl(signedUrl)

  return (
    <>
      <div>
        <div className='space-y-2'>
          <div
            key={`${signedUrl}`}
            className='flex gap-4 items-center justify-between cursor-pointer p-3 bg-gray-50 dark:bg-gray-800 rounded-lg'
          >
            <div
              onClick={() => {
                setShowFileViewer(true)
              }}
              className='flex items-center space-x-3 flex-1 min-w-0'
            >
              <div className='flex-shrink-0'>
                <div className='h-8 w-8 rounded bg-gray-100 dark:bg-gray-700 flex items-center justify-center'>
                  {FileIcon as React.ReactNode}
                </div>
              </div>
              <div className='flex-1 min-w-0  '>
                <p className='text-sm font-medium text-gray-900 dark:text-gray-100 truncate max-w-[1000px]'>
                  {extractFileNameFromUrl(signedUrl)}
                </p>
              </div>
            </div>
            <div className='flex items-center gap-2'>
              <Button
                type='button'
                variant='outline'
                size='sm'
                onClick={() => {
                  setShowFileViewer(true)
                }}
                className='flex-shrink-0'
              >
                <Download className='h-4 w-4' />
              </Button>
              {!hideRemoveButton ? (
                <Button
                  type='button'
                  variant='outline'
                  size='icon'
                  className="text-destructive hover:text-destructive/90 hover:bg-destructive/10 [&_svg:not([class*='size-'])]:size-4"
                  onClick={() => setOpenDeleteModal(true)}
                  title='Remove file'
                  disabled={deleting}
                >
                  {deleting ? <LoaderCircle className='size-4 animate-spin' /> : <Trash2 />}
                </Button>
              ) : null}
            </div>
          </div>
        </div>
      </div>

      <URLViewerModal
        isFileViewerNestedModal={true}
        open={showFileViewer}
        setOpen={setShowFileViewer}
        url={signedUrl}
      />

      <DeleteModal
        open={openDeleteModal}
        setOpen={setOpenDeleteModal}
        onDelete={async () => {
          if (onDelete) {
            await onDelete()
            if (onSuccess) onSuccess()
          }
        }}
        loading={deleting || false}
      />
    </>
  )
}
